// #527: Mails im Team verteilen – „In Bearbeitung“ (damit nicht zwei Kolleginnen gleichzeitig dieselbe
// Mail bearbeiten) und eine eigene Farbe je zuständigem Team/Mitarbeiter in der Liste. Reine Funktionen.

/** Nach dieser Zeit gilt eine vergessene „In Bearbeitung“-Markierung als abgelaufen und darf übernommen werden. */
export const BEARBEITUNG_STUNDEN = 8;

export interface Bearbeitung { vonId: string; seit: string }

/** Laufende Bearbeitung einer Mail (abgelaufene oder fehlende → null). */
export function aktiveBearbeitung(
  e: { in_bearbeitung_von?: string | null; in_bearbeitung_seit?: string | null },
  jetzt: Date,
): Bearbeitung | null {
  if (!e.in_bearbeitung_von || !e.in_bearbeitung_seit) return null;
  const seit = Date.parse(e.in_bearbeitung_seit);
  if (Number.isNaN(seit) || jetzt.getTime() - seit > BEARBEITUNG_STUNDEN * 3_600_000) return null;
  return { vonId: e.in_bearbeitung_von, seit: e.in_bearbeitung_seit };
}

/** Grenze für das bedingte Übernehmen: ältere Markierungen dürfen überschrieben werden. */
export function ablaufGrenze(jetzt: Date): string {
  return new Date(jetzt.getTime() - BEARBEITUNG_STUNDEN * 3_600_000).toISOString();
}

/** „seit 5 Min.“, „seit 2 Std.“ */
export function seitText(seit: string, jetzt: Date): string {
  const min = Math.max(0, Math.round((jetzt.getTime() - Date.parse(seit)) / 60_000));
  if (min < 1) return 'gerade eben';
  if (min < 60) return `seit ${min} Min.`;
  return `seit ${Math.floor(min / 60)} Std.`;
}

/** Vorname reicht in der Liste („Sarah Röhr“ → „Sarah“). */
export function kurzname(name: string | null | undefined): string {
  const n = (name ?? '').trim();
  return n.split(/\s+/)[0] || 'Kollege';
}

// ── Farben je Zuständigem ─────────────────────────────────────────────────────

export interface ZustaendigFarbe { fg: string; bg: string }

/** Gut unterscheidbare Töne, die in Hell und Dunkel lesbar sind (Systemfarben wie im übrigen Design). */
export const ZUSTAENDIG_PALETTE: readonly string[] = [
  '#0a84ff', '#34c759', '#ff9f0a', '#bf5af2', '#ff375f', '#30b0c7', '#5e5ce6', '#a2845e', '#ffd60a', '#64d2ff',
];

const zuFarbe = (hex: string): ZustaendigFarbe => ({ fg: hex, bg: `color-mix(in srgb, ${hex} 13%, transparent)` });

/**
 * Feste Farbe je Team/Mitarbeiter: Teams zuerst, dann Mitarbeiter, jeweils nach Name sortiert → jede Person
 * behält ihre Farbe, solange sich die Liste nicht ändert. Ab dem 11. Eintrag wiederholen sich die Farben.
 */
export function zustaendigFarben(
  teams: readonly { id: string; name: string }[],
  mitarbeiter: readonly { id: string; full_name: string }[],
): Map<string, ZustaendigFarbe> {
  const sort = <T,>(l: readonly T[], n: (x: T) => string) => [...l].sort((a, b) => n(a).localeCompare(n(b), 'de'));
  const ids = [...sort(teams, t => t.name).map(t => t.id), ...sort(mitarbeiter, m => m.full_name).map(m => m.id)];
  return new Map(ids.map((id, i) => [id, zuFarbe(ZUSTAENDIG_PALETTE[i % ZUSTAENDIG_PALETTE.length]!)]));
}

/** Zuständigkeit einer Mail: eigene Zuweisung, sonst die des zugeordneten Objekts. */
export function effektiveZustaendigkeit(e: {
  zustaendig_id?: string | null; zustaendig_typ?: string | null;
  objekt?: { zustaendig_id?: string | null; zustaendig_typ?: string | null } | null;
}): { id: string; typ: 'team' | 'mitarbeiter' } | null {
  const id = e.zustaendig_id ?? e.objekt?.zustaendig_id ?? null;
  if (!id) return null;
  const typ = e.zustaendig_id ? e.zustaendig_typ : e.objekt?.zustaendig_typ;
  return { id, typ: typ === 'team' ? 'team' : 'mitarbeiter' };
}
